// Client-side mirror of the running-order predicates in
// backend/utils/ongoingOrders.js.
//
// Running-order aggregation is a FACT about an order, not one of its lifecycle
// statuses: a running order also ships, in parts, so it holds an ordinary status
// (confirmed / partially_shipped / shipped) while its aggregation state lives in
// these two columns.
//
//   open running order   = aggregates_until_closed = 1 AND completed_at  IS NULL
//   closed running order = aggregates_until_closed = 1 AND completed_at IS NOT NULL
//
// Deriving "closed" from completed_at (rather than a 'complete' status) means the
// two facts cannot drift apart, and it is the same rule the monthly-invoice month
// key already uses (COALESCE(completed_at, order_date)).

// Does this order take part in running-monthly aggregation at all?
export function isRunningOrder(order) {
  return Number(order?.aggregates_until_closed) === 1;
}

// Still accepting batches and still closable.
export function isOpenRunningOrder(order) {
  return isRunningOrder(order) && !order?.completed_at;
}

// Closed, ready for invoicing, and no longer accepting batches or adjustments.
export function isClosedRunningOrder(order) {
  return isRunningOrder(order) && !!order?.completed_at;
}

// A running order's identity, mirrored from findOpenOngoingOrder in
// backend/utils/ongoingOrders.js: customer + billing address + destination.
//
// The destination is part of the key because an order carries ONE
// shipping_address_id. Keying on the billing address alone would make a batch meant
// for a different address join an order that ships somewhere else. Keying here
// instead means a customer with two delivery addresses has two independent running
// orders, and the UI always shows the same one the server will append to.
//
// A pickup order has a NULL shipping_address_id, so it is keyed distinctly from any
// delivery — the same way the server separates them.
export function runningOrderKey(order) {
  return `${order?.billing_address_id || ''}|${order?.shipping_address_id || ''}`;
}

// The open running order a batch for these addresses would be appended to, or null.
// `destinationShippingId` is the shipping address as it will be persisted, i.e. null
// for pickup.
export function matchOngoingOrder(ongoingOrders, { billingAddressId, shippingAddressId }) {
  const wanted = runningOrderKey({
    billing_address_id: billingAddressId || null,
    shipping_address_id: shippingAddressId || null,
  });
  return (ongoingOrders || []).find(o => isOpenRunningOrder(o) && runningOrderKey(o) === wanted) || null;
}
